import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { MethodPicker } from '@/components/admin/MethodPicker'
import { ProcessAttributeDefsDialog } from './ProcessAttributeDefsDialog'
import { useCapabilityOptions, useStepMethods } from '@/hooks/useResourceCapabilities'
import { useProcessFieldDefs, useProcessStepConnection, useUpsertProcessStep } from '@/hooks/useProcessSteps'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import {
  ATTRIBUTE_TYPE_LABELS,
  attributeType,
  attributesDirty,
  draftFrom,
  entryForFlag,
  entryForValue,
  flagAnswer,
  PROCESS_OUTPUT_KEY,
  sameMethodSet,
  type AttributeEntry,
  type AttributeType,
} from '@/lib/processAttributes'
import {
  acceptAttr,
  coerceFieldValue,
  describeFieldRules,
  validateFieldValue,
} from '@/lib/fieldValidation'
import type { ProcessFieldDef } from '@/api/processStepsApi'

/**
 * Field Attributes for one process step.
 *
 * A DEFINED SET, not one control: the definitions come from
 * `GET /process-steps/field-defs`, each carrying the data type that picks its
 * widget and the validation applied on both sides. Values are stored in
 * `ProcessStepConnection.attributes` as `{ active, value }` keyed by the
 * definition's `key` — the same shape a material's attributes use, and the shape
 * the operator's runtime Attributes tab already renders as chips.
 *
 * **Method is the seeded first attribute and the one exception.** It reads like
 * any other enum, but its value is membership in `MethodProcessStep`, saved
 * through the step's own methods endpoint — because the Methods view answers
 * "which processes does this method cover" off those rows and Last Date Used
 * joins through them. A copy in the attributes map would drift from both, which
 * is exactly what the original `attributes.method` design got wrong.
 *
 * Adding another attribute therefore needs no work here: seed a definition and
 * it renders.
 */
export function FieldAttributesTab({ fieldId }: { fieldId: string }) {
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.processEdit)

  const defsQuery = useProcessFieldDefs()
  const connection = useProcessStepConnection(fieldId)
  const upsert = useUpsertProcessStep()
  const capabilities = useCapabilityOptions()
  const stepMethods = useStepMethods(fieldId)

  const defs = defsQuery.data ?? []
  const stored = connection.data?.attributes as Record<string, unknown> | undefined
  const savedMethodIds = stepMethods.list.data?.map(m => m.id) ?? []

  const [draft, setDraft] = useState<Record<string, AttributeEntry> | null>(null)
  const [methodDraft, setMethodDraft] = useState<string[] | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [managing, setManaging] = useState(false)

  // A different step is a different set of values; carrying a draft across would
  // save one step's attributes onto another.
  useEffect(() => {
    setDraft(null)
    setMethodDraft(null)
    setErrors({})
    setError(null)
    setSaved(false)
  }, [fieldId])

  const values = draft ?? draftFrom(defs, stored)
  const methodIds = methodDraft ?? savedMethodIds
  // One table per attribute TYPE — the left one is the attribute-flags, the
  // right one the attribute-values. The type comes off the definition, so
  // seeding one more attribute needs no work here: it lands in the table its
  // type puts it in.
  const flagDefs = defs.filter(def => attributeType(def) === 'attribute-flag')
  const valueDefs = defs.filter(def => attributeType(def) === 'attribute-value')

  // Both tables edit ONE draft, so an attribute's table is a layout question
  // and nothing more: Save writes the whole set either way.
  const onEntryChange = (def: ProcessFieldDef, next: AttributeEntry) => {
    setSaved(false)
    setErrors(prev => ({ ...prev, [def.key]: '' }))
    setDraft({ ...values, [def.key]: next })
  }
  const onMethodsChange = (next: string[]) => {
    setSaved(false)
    setMethodDraft(next)
  }
  const dirty =
    (draft !== null && attributesDirty(defs, stored, values)) ||
    (methodDraft !== null && !sameMethodSet(methodDraft, savedMethodIds))

  const loading = defsQuery.isLoading || connection.isLoading || stepMethods.list.isLoading
  const busy = upsert.isPending || stepMethods.save.isPending

  if (loading) {
    return (
      <div className='flex items-center gap-2 p-5 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading…
      </div>
    )
  }

  if (defs.length === 0) {
    // Nothing defined at all — only reachable on a tenant whose seed has not
    // run. Still offers the way in, so it is a dead end nobody can fix.
    return (
      <div className='space-y-3 p-5'>
        <p className='text-sm text-muted-foreground'>No attributes are defined for processes.</p>
        {canManage && (
          <>
            <Button variant='outline' size='sm' onClick={() => setManaging(true)}>
              Manage attributes
            </Button>
            <ProcessAttributeDefsDialog
              open={managing}
              onClose={() => setManaging(false)}
              defs={defs}
              canManage={canManage}
            />
          </>
        )}
      </div>
    )
  }

  return (
    <div className='space-y-5 p-5'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h3 className='text-sm font-semibold text-foreground'>Field Attributes</h3>
          <p className='mt-0.5 text-sm text-muted-foreground'>
            What this process records. Shown to the operator when they run it.
          </p>
        </div>
        {/* The set itself is edited here, not under Configure: the need surfaces
            while filling one in, and this needs no new navigation to reach. */}
        {canManage && (
          <Button variant='outline' size='sm' onClick={() => setManaging(true)}>
            Manage attributes
          </Button>
        )}
      </div>

      <div className='max-w-6xl space-y-4'>
        {/*
          ONE TABLE PER ATTRIBUTE TYPE: the attribute-flags on the left, the
          attribute-values on the right. That is the client's model of the set,
          and each table's single column is the answer its type takes —
          "YES/NO is the icon", and "attribute on the right: no flag, only
          value".

          Every attribute used to sit in ONE table with both a Flag column and a
          Value column, which asked each of them for two answers where it has
          one and read as a per-step enable/disable that does not exist: the
          flag beside a value could only repeat or contradict the box, and the
          "Yes"/"No" printed beside a flag's switch said the same thing twice.
        */}
        <div className='grid gap-4 lg:grid-cols-2'>
          {flagDefs.length > 0 && (
            <AttributeTable
              type='attribute-flag'
              defs={flagDefs}
              canManage={canManage && !busy}
              errors={errors}
              values={values}
              methodIds={methodIds}
              methodOptions={capabilities.data?.methods ?? []}
              onEntryChange={onEntryChange}
              onMethodsChange={onMethodsChange}
            />
          )}
          {valueDefs.length > 0 && (
            <AttributeTable
              type='attribute-value'
              defs={valueDefs}
              canManage={canManage && !busy}
              errors={errors}
              values={values}
              methodIds={methodIds}
              methodOptions={capabilities.data?.methods ?? []}
              onEntryChange={onEntryChange}
              onMethodsChange={onMethodsChange}
            />
          )}
        </div>

        {error && <p className='text-sm font-medium text-red-600'>{error}</p>}

        {canManage && (
          <div className='flex items-center gap-2'>
            <Button size='sm' disabled={!dirty || busy} onClick={() => void commit()}>
              {busy && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
              Save
            </Button>
            {dirty && (
              <Button
                size='sm'
                variant='ghost'
                onClick={() => {
                  setDraft(null)
                  setMethodDraft(null)
                  setErrors({})
                }}
              >
                Cancel
              </Button>
            )}
            {saved && !dirty && <span className='text-xs text-muted-foreground'>Saved</span>}
          </div>
        )}

        <ProcessAttributeDefsDialog
          open={managing}
          onClose={() => setManaging(false)}
          defs={defs}
          canManage={canManage}
        />

        {/* Names the other editor, so two places to set one thing do not read as
            two different settings. */}
        <p className='text-xs text-muted-foreground'>
          Method is also editable per method under{' '}
          <Link to='/configure?tab=method&view=methods' className='underline'>
            Configure → Method
          </Link>
          .
        </p>
      </div>
    </div>
  )

  async function commit() {
    setError(null)

    // Validate before either write: a half-applied save would leave the methods
    // changed and the attributes not, with no way to tell from the screen.
    const found: Record<string, string> = {}
    const payload: Record<string, AttributeEntry> = {}
    for (const def of defs) {
      if (def.relational) continue
      const entry = values[def.key]
      if (!entry) continue
      // An attribute-flag records its answer whichever way it points: NO is an
      // answer, so this never writes the step out of the attribute.
      if (attributeType(def) === 'attribute-flag') {
        payload[def.key] = entryForFlag(flagAnswer(entry))
        continue
      }
      // An attribute-value records what was entered, and an empty box is
      // nothing to record — so it validates as nothing rather than as a
      // violated `required` rule that nobody could clear.
      const value = coerceFieldValue(def.dataType, entry.value)
      const next = entryForValue(value)
      if (next.active) {
        const message = validateFieldValue(def, value)
        if (message) found[def.key] = message
      }
      payload[def.key] = next
    }
    if (Object.keys(found).length > 0) {
      setErrors(found)
      return
    }
    setErrors({})

    try {
      if (draft !== null && attributesDirty(defs, stored, values)) {
        await upsert.mutateAsync({ fieldId, payload: { attributes: payload } })
      }
      if (methodDraft !== null && !sameMethodSet(methodDraft, savedMethodIds)) {
        await stepMethods.save.mutateAsync(methodDraft)
      }
      setDraft(null)
      setMethodDraft(null)
      setSaved(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the attributes.')
    }
  }
}

/**
 * One table: the attributes of one kind, and the single column they answer.
 *
 * The header names that column, and every control in it is the same width and
 * in the same place on every row — which is what makes a set of these read as a
 * list rather than as one form per attribute.
 */
function AttributeTable({
  type,
  defs,
  values,
  errors,
  canManage,
  methodIds,
  methodOptions,
  onEntryChange,
  onMethodsChange,
}: {
  type: AttributeType
  defs: ProcessFieldDef[]
  values: Record<string, AttributeEntry>
  errors: Record<string, string>
  canManage: boolean
  methodIds: string[]
  methodOptions: { id: string; name: string; stepCount: number }[]
  onEntryChange: (def: ProcessFieldDef, next: AttributeEntry) => void
  onMethodsChange: (next: string[]) => void
}) {
  return (
    <div className='overflow-hidden self-start rounded-lg border'>
      <div className='flex items-center gap-6 border-b bg-muted/50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        <span className='min-w-0 flex-1'>Attribute</span>
        {/* The column is named for the TYPE it holds, which is also the answer
            every row in it gives. */}
        <span
          className={
            type === 'attribute-flag' ? 'w-16 shrink-0 text-center' : 'w-52 shrink-0'
          }
        >
          {ATTRIBUTE_TYPE_LABELS[type]}
        </span>
      </div>
      <div className='divide-y'>
        {defs.map(def => (
          <AttributeRow
            key={def.key}
            def={def}
            canManage={canManage}
            error={errors[def.key]}
            entry={values[def.key]}
            methodIds={methodIds}
            methodOptions={methodOptions}
            onEntryChange={next => onEntryChange(def, next)}
            onMethodsChange={onMethodsChange}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * One attribute: what it is, and the ONE control its TYPE answers it with.
 *
 * An attribute-flag gets a switch and nothing else — the switch is the yes/no,
 * which is why the "Yes"/"No" that used to sit beside it printed the same thing
 * twice. An attribute-value gets its value control and nothing else. Neither
 * switches the attribute in or out of the step: the row always has an answer.
 */
function AttributeRow({
  def,
  entry,
  canManage,
  error,
  methodIds,
  methodOptions,
  onEntryChange,
  onMethodsChange,
}: {
  def: ProcessFieldDef
  entry: AttributeEntry | undefined
  canManage: boolean
  error?: string
  methodIds: string[]
  methodOptions: { id: string; name: string; stepCount: number }[]
  onEntryChange: (next: AttributeEntry) => void
  onMethodsChange: (next: string[]) => void
}) {
  const rules = describeFieldRules(def)
  const flag = attributeType(def) === 'attribute-flag'
  /**
   * ONE line, and the rules are not on it.
   *
   * Joining description and rules ran four of the ten seeded rows onto a second
   * line, and rows of two different heights are what stops a list reading as a
   * list. The rules go in the control's placeholder instead, which is both
   * shorter and where they are actually wanted — "whole number, 0 or more"
   * belongs in the box you type the number into. A def with no description
   * still shows them here rather than leaving the row unexplained.
   */
  const meta = def.description || rules

  return (
    <div className='flex items-center justify-between gap-6 px-4 py-3'>
      <div className='min-w-0 flex-1'>
        {/* Never dimmed by its own answer: a flag pointing at No and a box left
            empty are answers about the step, not the attribute being excluded
            from it. */}
        <label className='text-sm font-medium'>{def.label}</label>
        {error ? (
          <p className='mt-0.5 text-xs font-medium text-red-600'>{error}</p>
        ) : (
          // Truncated rather than wrapped: a description is author-typed and
          // has no length limit, and the full text is on hover.
          meta && (
            <p className='mt-0.5 truncate text-xs text-muted-foreground' title={meta}>
              {meta}
            </p>
          )
        )}
        {def.relational && methodOptions.length === 0 && (
          <p className='mt-0.5 text-xs text-muted-foreground'>
            No methods yet. Create one under{' '}
            <Link to='/configure?tab=method&view=methods' className='underline'>
              Configure → Method
            </Link>
            .
          </p>
        )}
      </div>

      {flag ? (
        <div className='flex w-16 shrink-0 justify-center'>
          {/* The icon IS the yes/no — there is no second control and nothing
              else the switch could mean. */}
          <Switch
            checked={flagAnswer(entry)}
            disabled={!canManage}
            onCheckedChange={next => onEntryChange(entryForFlag(next))}
            aria-label={`${def.label} yes or no`}
          />
        </div>
      ) : (
        <div className='w-52 shrink-0'>
          {def.relational ? (
            <MethodPicker
              options={methodOptions}
              value={methodIds}
              onChange={onMethodsChange}
              disabled={!canManage}
            />
          ) : (
            <AttributeInput
              def={def}
              entry={entry}
              // Only when it is not already the row's one meta line.
              placeholder={def.description ? (rules ?? '') : ''}
              disabled={!canManage}
              // Never gated on a flag, because there is none: what is in the
              // box is the whole answer.
              onChange={value => onEntryChange(entryForValue(value))}
            />
          )}
        </div>
      )}
    </div>
  )
}

/** The widget for a data type. Mirrors what ProfilePopup renders for a material. */
function AttributeInput({
  def,
  entry,
  disabled,
  placeholder,
  onChange,
}: {
  def: ProcessFieldDef
  entry: AttributeEntry | undefined
  disabled: boolean
  /** The def's rules, when the row is not already showing them. */
  placeholder?: string
  onChange: (value: unknown) => void
}) {
  const value = entry?.value

  // Every control in this column is h-9, so the rows line up: `Input` is h-10
  // by default and `MethodPicker`'s trigger is h-9, and a column mixing the two
  // reads as rows of different heights rather than as a list.
  //
  // No `boolean` branch: an attribute-flag never reaches here — its switch is
  // its whole answer, and it sits in the Flag table rather than this column.
  if (def.dataType === 'enum') {
    // Single-select here; a `multiple` enum other than method would need a
    // picker of its own, and none exists yet.
    return (
      <SearchableSelect
        value={typeof value === 'string' ? value : ''}
        onChange={onChange}
        options={def.options.map(o => ({ value: o, label: o }))}
        // Process Output unset IS Process (z8nrz7f1dz), so say so rather than ask.
        placeholder={
          def.key === PROCESS_OUTPUT_KEY ? 'Process' : def.options.length ? 'Select…' : 'Nothing configured'
        }
        disabled={disabled || def.options.length === 0}
        emptyMessage='No matching options.'
      />
    )
  }

  return (
    <Input
      type={def.dataType === 'number' ? 'number' : def.dataType === 'date' ? 'date' : 'text'}
      accept={def.dataType === 'file' ? acceptAttr(def) : undefined}
      value={value === null || value === undefined ? '' : String(value)}
      placeholder={placeholder || undefined}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
      className='h-9'
    />
  )
}
